import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import {DragDropContext, Draggable, Droppable} from "react-beautiful-dnd";

import FormPopup from "../../../components/common/popup/form-popup";
import DragIcon from "../../../components/icons/drag-icon";

const DocumentBodyReorderModal = ({
    isOpen,
    onRequestClose,
    rows,
    onConfirm,
    isSubmitting,
}) => {
    const {t} = useTranslation();
    const [draftRows, setDraftRows] = useState(() => Array.isArray(rows) ? [...rows] : []);
    const [selectedRowIds, setSelectedRowIds] = useState(() => new Set());
    const rowKeyRegistryRef = useRef({
        counter: 0,
        map: typeof WeakMap === "function" ? new WeakMap() : null,
    });

    const resolveRowKey = useCallback((row, fallbackIndex) => {
        if (!row) {
            return fallbackIndex !== undefined && fallbackIndex !== null
                ? `__row_fallback_${fallbackIndex}`
                : "";
        }

        const candidateValues = [
            row.id,
            row.body_id,
            row.bodyId,
            row.row_id,
            row.rowId,
            row.document_body_row_id,
            row.document_body_id,
            row.uuid,
            row.key,
            row.temp_id,
            row.position,
            row.product_id,
            row.productId,
            row.product?.id,
            row.product?.uuid,
        ];

        for (let index = 0; index < candidateValues.length; index += 1) {
            const candidate = candidateValues[index];
            if (candidate !== undefined && candidate !== null && candidate !== "") {
                return String(candidate);
            }
        }

        const registry = rowKeyRegistryRef.current;
        if (registry.map) {
            if (!registry.map.has(row)) {
                registry.counter += 1;
                registry.map.set(row, `__temp_row_key_${registry.counter}`);
            }
            return registry.map.get(row);
        }
        registry.counter += 1;
        return `__temp_row_key_${registry.counter}`;
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setDraftRows(Array.isArray(rows) ? [...rows] : []);
        setSelectedRowIds(new Set());
    }, [isOpen, rows]);

    useEffect(() => {
        setSelectedRowIds((previous) => {
            if (!(previous instanceof Set) || previous.size === 0) {
                return previous instanceof Set ? previous : new Set();
            }
            const validKeys = new Set(draftRows.map((row, index) => resolveRowKey(row, index)));
            let needsCleanup = false;
            previous.forEach((key) => {
                if (!validKeys.has(key)) {
                    needsCleanup = true;
                }
            });
            if (!needsCleanup) {
                return previous;
            }
            const next = new Set();
            validKeys.forEach((key) => {
                if (previous.has(key)) {
                    next.add(key);
                }
            });
            return next;
        });
    }, [draftRows, resolveRowKey]);

    const handleDragEnd = useCallback((result) => {
        if (!result.destination || result.destination.index === result.source.index) {
            return;
        }
        const destinationIndex = result.destination.index;
        const sourceIndex = result.source.index;
        setDraftRows((previous) => {
            const sourceRow = previous[sourceIndex];
            if (!sourceRow) {
                return previous;
            }
            const sourceKey = resolveRowKey(sourceRow, sourceIndex);
            const isMultiDrag = selectedRowIds instanceof Set && selectedRowIds.has(sourceKey);
            const movingKeys = new Set(
                isMultiDrag
                    ? Array.from(selectedRowIds)
                    : [sourceKey]
            );
            const movingRows = previous.filter((row, index) => movingKeys.has(resolveRowKey(row, index)));
            if (movingRows.length === 0) {
                return previous;
            }
            const remainingRows = previous.filter((row, index) => !movingKeys.has(resolveRowKey(row, index)));
            const removedBeforeDestination = previous
                .slice(0, destinationIndex)
                .filter((row, index) => movingKeys.has(resolveRowKey(row, index))).length;
            let insertIndex = destinationIndex - removedBeforeDestination;
            if (insertIndex < 0) {
                insertIndex = 0;
            }
            if (insertIndex > remainingRows.length) {
                insertIndex = remainingRows.length;
            }
            const next = [...remainingRows];
            next.splice(insertIndex, 0, ...movingRows);
            return next;
        });
    }, [resolveRowKey, selectedRowIds]);

    const handleToggleRowSelection = useCallback((rowKey) => {
        setSelectedRowIds((previous) => {
            const next = new Set(previous instanceof Set ? previous : []);
            if (next.has(rowKey)) {
                next.delete(rowKey);
            } else {
                next.add(rowKey);
            }
            return next;
        });
    }, []);

    const handleSelectAllToggle = useCallback(() => {
        setSelectedRowIds((previous) => {
            if (draftRows.length === 0) {
                return new Set();
            }
            const everySelected = previous instanceof Set && previous.size === draftRows.length;
            if (everySelected) {
                return new Set();
            }
            return new Set(draftRows.map((row, index) => resolveRowKey(row, index)));
        });
    }, [draftRows, resolveRowKey]);

    const handleClearSelection = useCallback(() => {
        setSelectedRowIds(new Set());
    }, []);

    const isEveryRowSelected = draftRows.length > 0 && selectedRowIds instanceof Set && selectedRowIds.size === draftRows.length;
    const selectedCount = selectedRowIds instanceof Set ? selectedRowIds.size : 0;

    const hasChanges = useMemo(() => {
        if (!Array.isArray(rows) || rows.length !== draftRows.length) {
            return true;
        }
        return draftRows.some((row, index) => resolveRowKey(row, index) !== resolveRowKey(rows[index], index));
    }, [draftRows, resolveRowKey, rows]);

    const resolveRowStatus = (row) => (
        row?.status
        ?? row?.product?.status_label
        ?? row?.product?.statusLabel
        ?? row?.product?.status
        ?? row?.product?.state
        ?? t("app.common.not_available_short", {defaultValue: "N/D"})
    );

    const handleClose = useCallback(() => {
        if (isSubmitting) {
            return;
        }
        if (typeof onRequestClose === "function") {
            onRequestClose();
        }
    }, [isSubmitting, onRequestClose]);

    const handleConfirm = useCallback(() => {
        if (typeof onConfirm === "function") {
            onConfirm(draftRows);
        }
    }, [draftRows, onConfirm]);

    const selectionLabel = useMemo(() => {
        if (selectedCount === 0) {
            return t("app.documents.body_reorder_no_selection", {defaultValue: "Nessuna riga selezionata"});
        }
        return t("app.documents.body_reorder_selection_count", {
            count: selectedCount,
            defaultValue: selectedCount === 1 ? "1 riga selezionata" : "{{count}} righe selezionate",
        });
    }, [selectedCount, t]);

    return (
        <FormPopup
            title={t("app.documents.body_reorder_title", {defaultValue: "Riordina righe"})}
            isOpen={isOpen}
            setIsOpen={(nextOpen) => {
                if (!nextOpen) {
                    handleClose();
                    return;
                }
            }}
        >
            <div className="space-y-4">
                <p className="text-sm text-gray-600">
                    {t("app.documents.body_reorder_description", {defaultValue: "Trascina le righe per modificare l'ordine del documento. Seleziona più righe per spostarle insieme."})}
                </p>
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-white px-3 py-2 text-xs text-gray-600 shadow-sm">
                    <span className="font-medium text-gray-700">
                        {selectionLabel}
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={handleSelectAllToggle}
                            className="rounded border border-gray-200 px-3 py-1 font-medium text-gray-600 hover:bg-gray-50"
                        >
                            {isEveryRowSelected
                                ? t("app.documents.body_reorder_unselect_all", {defaultValue: "Deseleziona tutto"})
                                : t("app.documents.body_reorder_select_all", {defaultValue: "Seleziona tutto"})}
                        </button>
                        <button
                            type="button"
                            onClick={handleClearSelection}
                            disabled={selectedCount === 0}
                            className="rounded border border-gray-200 px-3 py-1 font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {t("app.documents.body_reorder_clear_selection", {defaultValue: "Azzera selezione"})}
                        </button>
                    </div>
                </div>
                <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-gray-200 bg-gray-50">
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="document-body-reorder-list">
                            {(provided) => (
                                <ul
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className="divide-y divide-gray-200"
                                >
                                    {draftRows.length === 0 && (
                                        <li className="px-4 py-6 text-center text-sm text-gray-500">
                                            {t("app.common.no_data", {defaultValue: "Nessun dato disponibile"})}
                                        </li>
                                    )}
                                    {draftRows.map((row, index) => {
                                        const rowKey = resolveRowKey(row, index);
                                        const statusLabel = resolveRowStatus(row);
                                        const rowSelected = selectedRowIds instanceof Set && selectedRowIds.has(rowKey);
                                        return (
                                            <Draggable key={rowKey || index} draggableId={rowKey || String(index)} index={index}>
                                                {(dragProvided, snapshot) => (
                                                    <li
                                                        ref={dragProvided.innerRef}
                                                        {...dragProvided.draggableProps}
                                                        className={`flex items-center gap-4 px-4 py-3 text-sm ${snapshot.isDragging ? "shadow-lg ring-2 ring-blue-200" : ""} ${rowSelected ? "bg-blue-50" : "bg-white"}`}
                                                    >
                                                        <div className="flex items-center">
                                                            <input
                                                                type="checkbox"
                                                                className="h-4 w-4 rounded border-gray-300 text-blue-1000 focus:ring-blue-1000"
                                                                checked={rowSelected}
                                                                onChange={() => handleToggleRowSelection(rowKey)}
                                                                onClick={(event) => event.stopPropagation()}
                                                                onMouseDown={(event) => event.stopPropagation()}
                                                                onTouchStart={(event) => event.stopPropagation()}
                                                            />
                                                        </div>
                                                        <div
                                                            {...dragProvided.dragHandleProps}
                                                            className="flex flex-1 items-center gap-4 cursor-grab"
                                                        >
                                                            <DragIcon className="h-4 w-4 text-gray-400" />
                                                            <span className="font-semibold text-gray-900">
                                                                #{row?.position ?? index + 1}
                                                            </span>
                                                            <div className="min-w-0">
                                                                <p className="truncate font-medium text-gray-800">
                                                                    {row?.name || t("app.common.unnamed", {defaultValue: "Senza nome"})}
                                                                </p>
                                                                <p className="text-xs text-gray-500">
                                                                    {statusLabel}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </li>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </ul>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="inline-flex items-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                        {t("app.common.cancel", {defaultValue: "Annulla"})}
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={!hasChanges || isSubmitting}
                        className="inline-flex items-center rounded-md border border-blue-1000 bg-blue-1000 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {isSubmitting
                            ? t("app.common.saving", {defaultValue: "Salvataggio..."})
                            : t("app.common.save", {defaultValue: "Salva"})}
                    </button>
                </div>
            </div>
        </FormPopup>
    );
};

DocumentBodyReorderModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    rows: PropTypes.arrayOf(PropTypes.object),
    onConfirm: PropTypes.func,
    isSubmitting: PropTypes.bool,
};

DocumentBodyReorderModal.defaultProps = {
    rows: [],
    onConfirm: undefined,
    isSubmitting: false,
};

export default DocumentBodyReorderModal;
